import { MenuItem } from "@/app/ui/navigation/types";
import TasksMenuItem from "@/components/controls/tasks/TasksMenuItem.vue";
import type { Location } from "vue-router";
import { EnumExternalCalendarProvider } from "@/app/services/generated/enums/enumExternalCalendarProvider.generated";
import { EnumFeatureFlag } from "@/app/services/generated/enums/enumFeatureFlag.generated";
import { EnumUserPermission } from "@/app/services/generated/enums/enumUserPermission.generated";
import { hasPermissionsUsingUserStore } from "@/components/pages/administration/user_roles/UserAccessHandler";

export const mainMenu = {
  home: "home",
  brewmanNews: "brewman_news",
  viewBrewmanNews: (): Location => ({
    name: mainMenu.brewmanNews,
  }),
  goHome: (): Location => ({ name: mainMenu.home }),
  tasks: "tasks",
  viewTasks: (): Location => ({
    name: mainMenu.tasks,
  }),
  search: "search",
  calendar: "calendar",
  viewCalendar: (): Location => ({ name: mainMenu.calendar }),
  calendarGoogleSuccess: "calendar_link_success",
  viewCalendarGoogleSuccess: (): Location => ({
    name: mainMenu.calendarGoogleSuccess,
  }),
  calendarMicrosoftSuccess: "calendar_microsoft_success",
  viewCalendarMicrosoftSuccess: (): Location => ({
    name: mainMenu.calendarMicrosoftSuccess,
  }),
  googleSheetSuccess: "sheet_link_success",
  viewGoogleSheetSuccess: (): Location => ({
    name: mainMenu.googleSheetSuccess,
  }),
};

export const buildMainMenuItems: () => MenuItem[] = () => [
  MenuItem.fromOpts({
    name: mainMenu.home,
    icon: "home",
    url: "/home",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "common" */ "@/components/pages/dashboard/Dashboard.vue"
      ),
  }),
  MenuItem.fromOpts({
    name: mainMenu.brewmanNews,
    url: "/news",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "common" */ "@/components/pages/news/BrewmanNews.vue"
      ),
    visible: false,
  }),
  MenuItem.fromOpts({
    name: mainMenu.calendar,
    icon: "calendar_month",
    url: "/calendar",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "common" */ "@/components/pages/calendar/Calendar.vue"
      ),
    visible: () =>
      hasPermissionsUsingUserStore([EnumUserPermission.menuCalendarView]),
  }),
  MenuItem.fromOpts({
    name: mainMenu.calendarGoogleSuccess,
    url: "/calendar-link-success/google",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "common" */ "@/components/pages/calendar/IntegrationLinkCallbackDialog.vue"
      ),
    componentProps: (route) => ({
      state: route.query.state,
      code: route.query.code,
      provider: EnumExternalCalendarProvider.google,
      integration: "calendar",
    }),
    visible: false,
  }),
  MenuItem.fromOpts({
    name: mainMenu.calendarMicrosoftSuccess,
    url: "/calendar-link-success/microsoft",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "common" */ "@/components/pages/calendar/IntegrationLinkCallbackDialog.vue"
      ),
    componentProps: (route) => ({
      state: route.query.state,
      code: route.query.code,
      provider: EnumExternalCalendarProvider.microsoft,
      integration: "calendar",
    }),
    visible: false,
  }),
  MenuItem.fromOpts({
    name: mainMenu.tasks,
    icon: "format_list_bulleted",
    url: "/tasks",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "tasks" */ "@/components/pages/tasks/ViewTasks.vue"
      ),
    menuComponent: TasksMenuItem,
    visible: () =>
      hasPermissionsUsingUserStore([EnumUserPermission.menuTasksView]),
  }),
  MenuItem.fromOpts({
    name: mainMenu.search,
    url: "/search",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "common" */ "@/components/pages/search/SearchResults.vue"
      ),
    visible: false,
    requiredFeatureFlag: EnumFeatureFlag.onlyDev,
  }),
  MenuItem.fromOpts({
    name: mainMenu.googleSheetSuccess,
    url: "/sheet-link-success",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "common" */ "@/components/pages/calendar/IntegrationLinkCallbackDialog.vue"
      ),
    componentProps: (route) => ({
      state: route.query.state,
      code: route.query.code,
      provider: EnumExternalCalendarProvider.google,
      integration: "sheets",
    }),
    visible: false,
  }),
];
